import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getFormTriggers,
  createFormTrigger,
  updateFormTrigger,
  deleteFormTrigger,
  sendReminderAPI,
} from "../services/formTriggerApi";
import { toast } from "react-hot-toast";

import CustomToaster from "../components/Toaster";
import { TOAST_TIME, TOAST_TITLE, TOAST_TYPE } from "../constants/constants";

// Fetch triggers
export const fetchTriggers = createAsyncThunk(
  "triggers/fetch",
  async ({ formId, page = 1 }, { rejectWithValue }) => {
    try {
      const response = await getFormTriggers(formId);
      return response.data;
    } catch (error) {
      toast.custom(
        <CustomToaster
          type={TOAST_TYPE.ERROR}
          message="Failed to fetch form triggers."
          time={TOAST_TIME.FIVE}
        />
      );
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Create trigger
export const createTrigger = createAsyncThunk(
  "triggers/create",
  async (data, { rejectWithValue }) => {
    try {
      const response = await createFormTrigger(data);
      toast.custom(
        <CustomToaster
          type={TOAST_TYPE.SUCCESS}
          title={TOAST_TITLE.SUCCESS}
          message="Trigger created successfully."
          time={TOAST_TIME.FIVE}
        />
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update trigger
export const updateTrigger = createAsyncThunk(
  "triggers/update",
  async (data, { rejectWithValue }) => {
    try {
      const response = await updateFormTrigger(data);
      toast.custom(
        <CustomToaster
          type={TOAST_TYPE.SUCCESS}
          title={TOAST_TITLE.SUCCESS}
          message="Trigger updated successfully."
          time={TOAST_TIME.FIVE}
        />
      );
      return response.data;
    } catch (error) {
      toast.custom(
        <CustomToaster
          type={TOAST_TYPE.ERROR}
          message="Failed to update trigger."
          time={TOAST_TIME.FIVE}
        />
      );
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Delete trigger
export const removeTrigger = createAsyncThunk(
  "triggers/delete",
  async (formTriggerId, { rejectWithValue }) => {
    try {
      await deleteFormTrigger(formTriggerId);
      toast.custom(
        <CustomToaster
          type={TOAST_TYPE.SUCCESS}
          title={TOAST_TITLE.SUCCESS}
          message="Trigger deleted successfully."
          time={TOAST_TIME.FIVE}
        />
      );
      return formTriggerId;
    } catch (error) {
      toast.custom(
        <CustomToaster
          type={TOAST_TYPE.ERROR}
          message="Failed to delete trigger."
          time={TOAST_TIME.FIVE}
        />
      );
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Send Reminder
export const sendReminder = createAsyncThunk(
  "triggers/sendReminder",
  async (payload, { rejectWithValue }) => {
    try {

      const response = await sendReminderAPI(payload);

      toast.custom(
        <CustomToaster
          type={TOAST_TYPE.SUCCESS}
          title={TOAST_TITLE.SUCCESS}
          message="Reminder sent successfully."
          time={TOAST_TIME.FIVE}
        />
      );

      return response.data;
    } catch (error) {
      toast.custom(
        <CustomToaster
          type={TOAST_TYPE.ERROR}
          message="Failed to send reminder."
          time={TOAST_TIME.FIVE}
        />
      );
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);


const triggerSlice = createSlice({
  name: "triggers",
  initialState: {
    list: [],
    loading: false,
    error: null,
    totalResults: 0,
    currentPage: 1,
    pageSize: 10,
  },
  reducers: {
    setPage: (state, action) => {
      state.currentPage = action.payload;
    },
    clearTriggers: (state) => {
      state.list = [];
      state.error = null;
      state.totalResults = 0;
      state.currentPage = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch triggers
      .addCase(fetchTriggers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTriggers.fulfilled, (state, action) => {
        state.loading = false;
        // normalize list if needed
        state.list = Array.isArray(action.payload.items) ? action.payload.items : action.payload;
        state.totalResults = action.payload.totalResults || state.list.length;
      })
      .addCase(fetchTriggers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create trigger
      .addCase(createTrigger.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTrigger.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(createTrigger.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update trigger
      .addCase(updateTrigger.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTrigger.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(updateTrigger.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete trigger
      .addCase(removeTrigger.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeTrigger.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(removeTrigger.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setPage, clearTriggers } = triggerSlice.actions;
export default triggerSlice.reducer;
